// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/feature-activity/plugin.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/plugins/src/activity/index.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo field-check-app
//     node fleet/fleet.ts check --repo field-check-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * `plugins/activity` — one folder, one plugin, everything it owns.
 *
 * ACTIVITY as a client kernel plugin (feature #1 of the plugin-kernel pilot).
 *
 * The whole client-side registration of the feature lives here: the feed
 * source, its teardown, and the navigation entry. An app that wants activity
 * adds one line to its composition and supplies two platform services.
 *
 * Criterion (VEREDICTO §1): a plugin, because it registers a live subscription
 * with the host and that subscription must die when the active account changes.
 */
import { createRoot } from "solid-js";
import { type Plugin, serviceToken } from "@fleet/kernel";
import {
  type ActivityFeedSource,
  type ActivityFeedSourceDeps,
  createActivityFeedSource,
} from "./feed-source";

/** Service published by this plugin. Typed: consumers never cast. */
export const ACTIVITY_FEED = serviceToken<ActivityFeedSource>("activity.feed");

/**
 * Services the host must publish before this plugin can mount. The names are
 * declared, so a composition missing one fails at mount time with a readable
 * message instead of at first render with `undefined is not a function`.
 */
/** What the host publishes under `PLATFORM_POWERSYNC`. */
export type PowerSyncService = Pick<ActivityFeedSourceDeps, "db" | "drizzle" | "userId">;

/** What the host publishes under `PLATFORM_ACTIVITY_HISTORY`. */
export type ActivityHistoryService = Pick<ActivityFeedSourceDeps, "fetchOlder">;

export const PLATFORM_POWERSYNC = serviceToken<PowerSyncService>("platform.powersync");
export const PLATFORM_ACTIVITY_HISTORY = serviceToken<ActivityHistoryService>(
  "platform.activity-history",
);

/**
 * `TUi` is the APP's contribution union (nav entries, keyboard commands…). The
 * plugin never looks inside it: it forwards whatever the composition put in
 * `contributes`, WITH its type. What matters is the coupling — if this plugin
 * is skipped, everything it contributes disappears with it, instead of leaving
 * a menu entry and a keyboard shortcut for a feature that is not there.
 */
export interface ActivityPluginConfig<TUi = never> {
  /**
   * What this app wants the feature to hand its UI. The labels, the icons and
   * the key bindings are product copy, so they live in the composition; what
   * lives here is that they only exist while the feature is mounted.
   */
  contributes?: readonly TUi[];
  localLimit?: number;
  pageSize?: number;
  /**
   * App-specific narrowing of the feed. Omitted by apps whose feed has no
   * dimensions; supplied reactively by apps that do (such as a shift
   * filter). The plugin does not interpret it — it forwards it.
   */
  filter?: ActivityFeedSourceDeps["filter"];
  matchesFilter?: ActivityFeedSourceDeps["matchesFilter"];
}

export function activityPlugin<TUi>(): Plugin<ActivityPluginConfig<TUi>, TUi> {
  return {
    id: "activity",
    requires: [PLATFORM_POWERSYNC, PLATFORM_ACTIVITY_HISTORY],
    provides: [ACTIVITY_FEED],
    // No local mirror, no feed: on an SSR render the plugin is skipped with a
    // reason and the nav entry never appears. No app writes that condition.
    capabilities: ["localDb"],

    data(ctx) {
      const powerSync = ctx.service(PLATFORM_POWERSYNC);
      const history = ctx.service(PLATFORM_ACTIVITY_HISTORY);

      // An explicit reactive root, so the kernel can dispose the whole thing on
      // an account switch. This is the ONE line about teardown in the entire
      // feature; nothing in any app repeats it.
      const source = createRoot((dispose) => {
        ctx.addCleanup(dispose);
        return createActivityFeedSource({
          db: powerSync.db,
          drizzle: powerSync.drizzle,
          userId: powerSync.userId,
          fetchOlder: history.fetchOlder,
          localLimit: ctx.config.localLimit,
          pageSize: ctx.config.pageSize,
        });
      });

      ctx.provide(ACTIVITY_FEED, source);
      ctx.log("feed_ready", { account: ctx.accountId });
    },

    frontend(ctx) {
      for (const item of ctx.config.contributes ?? []) {
        ctx.contribute(item);
      }
    },
  };
}

export type { ActivityFeedSource };

// The feature's data half is part of this plugin, so its public pieces are
// re-exported here: one folder, one entry point.
export {
  createActivityFeedSource,
  DEFAULT_API_PAGE_SIZE,
  DEFAULT_LOCAL_FEED_LIMIT,
  mapApiActivityRow,
  type ActivityFeedSourceDeps,
  type ActivityHistoryPage,
  type ActivityRow,
  type ApiActivityRow,
} from "./feed-source";
